/**
 * iOS/iPadOS Web Audio unlock utility.
 *
 * On Safari (especially iPadOS) the AudioContext stays suspended even after
 * ctx.resume() — the context needs a short silent buffer played during a
 * trusted user-gesture call-stack to truly transition to "running".
 *
 * Call unlockAudioContext() from any click / touchstart handler.
 * It is safe to call repeatedly; once unlocked it becomes a no-op.
 */

import { Howler } from 'howler'

let unlocked = false

function playSilentBuffer(ctx: AudioContext): void {
  const buf = ctx.createBuffer(1, 1, 22050)
  const src = ctx.createBufferSource()
  src.buffer = buf
  src.connect(ctx.destination)
  src.start(0)
}

export function unlockAudioContext(): void {
  if (unlocked) return
  try {
    const ctx = Howler.ctx as AudioContext | undefined
    if (!ctx) return

    if (ctx.state === 'suspended') {
      ctx.resume()
    }

    playSilentBuffer(ctx)

    if (ctx.state === 'running') {
      unlocked = true
    } else {
      ctx.addEventListener('statechange', () => {
        if (ctx.state === 'running') unlocked = true
      }, { once: true })
    }
  } catch { /* ignore */ }
}

export function isAudioContextUnlocked(): boolean {
  return unlocked
}

let listenerAttached = false

/**
 * Attach a one-time document-level touch/click listener that unlocks the
 * AudioContext on the very first user interaction. Safe to call multiple
 * times — only one set of listeners is ever registered.
 */
export function ensureUnlockListeners(): void {
  if (listenerAttached) return
  listenerAttached = true

  const handler = () => {
    unlockAudioContext()
    document.removeEventListener('touchstart', handler, true)
    document.removeEventListener('touchend', handler, true)
    document.removeEventListener('click', handler, true)
  }

  document.addEventListener('touchstart', handler, { capture: true, once: false })
  document.addEventListener('touchend', handler, { capture: true, once: false })
  document.addEventListener('click', handler, { capture: true, once: false })
}
